// =============================================================================
// store.ts – Centralized application state
// =============================================================================

import { CFG } from "../data/config.js";
import { LINE_CONFIG } from "./line-config.js";
import { sanitizeSettings } from "./settings-logic";
import { getCurrentUser, saveToCloud } from "./firebase-sync.js";
import { getNotificationConfig, saveNotificationConfig } from "./notifications.js";
import { expandProfile } from "./profile-expander";
import type { Settings, AppConfig, AppState, LineDataMap } from "./types.js";

// Dynamically expand all stop profiles at load time
if (CFG.stopProfiles) {
  const stopProfiles = CFG.stopProfiles as Record<string, any>;
  for (const lineId of Object.keys(stopProfiles)) {
    stopProfiles[lineId] = expandProfile(stopProfiles[lineId], lineId);
  }
}

// ─── State ──────────────────────────────────────────────────────────────────

const state: AppState = {
  currentTab: "timetable",
  timetableLine: null,
  timetableDirection: "outbound",
  timetableDayType: null,
  settingsPanelLine: null,
  settingsLineDirection: "outbound",
  settingsLineDay: "weekday",
  liveStopFilter: null,
  liveLineFilter: null,
  showAllStops: false,
  showAllLines: false,
  timetableShowAllLines: false,
  linesLoaded: false,
  trainsStation: null,
  trainsDirection: "to_milano",
  trainsShowAllStations: false,
  journey: null,
  timetableFilterWindow: (() => {
    const v =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("trasporti_timetable_filter_window")
        : null;
    // Default to the next 2h of departures (instead of the whole day) so the
    // Orari tab isn't several screens long. Users can widen via the 15/30/60/Tutto filter.
    if (!v) return 120;
    if (v === "all") return "all";
    return parseInt(v, 10);
  })(),
  timetableClassicView:
    typeof localStorage !== "undefined"
      ? localStorage.getItem("trasporti_timetable_classic_view") === "true"
      : false,
  // V2: which trip cards are currently expanded. PatchDOM's updateAttributes
  // strips the .expedited class on the 60s auto-refresh, so we track expansion
  // here and re-apply the class in renderTripCardFigma. Cleared on
  // line/direction/day change in renderTimetable (see _timetableLastScrollSignature).
  timetableExpandedTrips: {} as Record<string, boolean>,
  // "Segui questa corsa": pinned trip survives across re-renders so the 60s
  // tick + signature changes don't drop it. Persisted in localStorage
  // (session-only would be wiped on reload, which defeats the point of
  // pinning a commute). Kept OUT of `settings` to avoid cloud sync — it's
  // a transient navigation helper, not a real preference.
  timetablePinnedTrip: (() => {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem("trasporti_timetable_pinned_trip");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })() as { lineId: string; dayType: string; direction: string; tripId: string } | null,
  _settings: null as Settings | null,
  get settings(): Settings {
    if (this._settings === null) this._settings = loadSettings();
    return this._settings!;
  },
  set settings(v: Settings) {
    this._settings = v;
  }
};

if (typeof window !== "undefined") {
  (window as any)._app_state = state;
}

// ─── Line Data ──────────────────────────────────────────────────────────────

let LINE_DATA: LineDataMap = {};

// ─── Subscribers ────────────────────────────────────────────────────────────

type SubscriberFn = () => void;
const _subscribers = new Set<SubscriberFn>();

export function subscribe(fn: SubscriberFn): () => void {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

export function emit(): void {
  for (const fn of _subscribers) {
    try {
      fn();
    } catch (e) {
      console.error("[store] Subscriber error:", e);
    }
  }
}

// ─── Cloud write suppression (B8) ──────────────────────────────────────────

let _suppressCloudWrite = false;

export function setSuppressCloudWrite(v: boolean): void {
  _suppressCloudWrite = !!v;
}

export function isCloudWriteSuppressed(): boolean {
  return _suppressCloudWrite;
}

// ─── Settings persistence ───────────────────────────────────────────────────

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem("trasporti_settings");
    if (raw) return sanitizeSettings(JSON.parse(raw), CFG as unknown as AppConfig);
  } catch (error) {
    console.warn("Preferenze non leggibili, uso i default.", error);
  }
  return sanitizeSettings({}, CFG as unknown as AppConfig);
}

export function saveSettings(partial: Partial<Settings>): void {
  const oldFocusCity = state.settings.focusCity;
  if (partial.driveCanegrate !== undefined) {
    const reach = partial.stationReachMinutes ? { ...partial.stationReachMinutes } : { ...state.settings.stationReachMinutes };
    reach.CN_FS = partial.driveCanegrate;
    partial.stationReachMinutes = reach;
  }
  state.settings = sanitizeSettings({ ...state.settings, ...partial }, CFG as unknown as AppConfig);
  try {
    localStorage.setItem("trasporti_settings", JSON.stringify(state.settings));
  } catch (error) {
    console.warn("Impossibile salvare le preferenze.", error);
  }

  if (partial.followedLinesByCity || partial.focusCity) {
    syncFollowedLinesWithVisible();
  }

  if (!_suppressCloudWrite && getCurrentUser()) {
    saveToCloud({ settings: state.settings, notifications: getNotificationConfig() });
  }

  const cityChanged = state.settings.focusCity !== oldFocusCity;
  emit();

  if (typeof document !== "undefined") {
    document.dispatchEvent(
      new CustomEvent("trasporti:state-changed", {
        detail: { cityChanged, oldFocusCity }
      })
    );
  }
}

function syncFollowedLinesWithVisible(): void {
  const cfg = CFG as any;
  const focusCity: string = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const followedLinesByCity = state.settings?.followedLinesByCity || {};
  let visibleLines: string[] = followedLinesByCity[focusCity];

  if (!Array.isArray(visibleLines) || visibleLines.length === 0) {
    visibleLines = cfg.focusCities?.[focusCity]?.lineOrder || cfg.lineOrder || [];
  }
  if (!visibleLines.length) return;

  const notifConfig = getNotificationConfig();
  const keepCanegrate = notifConfig.followedLines.includes("canegrate");
  notifConfig.followedLines = [...visibleLines];
  if (keepCanegrate) notifConfig.followedLines.push("canegrate");
  saveNotificationConfig(notifConfig);
}

// ─── Active config resolution ───────────────────────────────────────────────

export function getActiveConfig(_state?: any, cfg?: any): any {
  const s = _state || state;
  const c = cfg || CFG;

  if (c.stopProfiles && !c.__stopProfilesExpanded) {
    for (const lineId of Object.keys(c.stopProfiles)) {
      c.stopProfiles[lineId] = expandProfile(c.stopProfiles[lineId], lineId);
    }
    c.__stopProfilesExpanded = true;
  }

  const focusCity: string = s?.settings?.focusCity || c?.defaults?.focusCity || "BT";
  const cityCfg = c?.focusCities?.[focusCity] || c?.focusCities?.["BT"];
  if (!cityCfg) return c;
  return {
    ...c,
    homeProfile: {
      ...c.homeProfile,
      address: cityCfg.homeProfile?.address || cityCfg.name || c.homeProfile.address,
      note:
        cityCfg.homeProfile?.note ||
        `Area di focus attiva: ${cityCfg.name || c.homeProfile.address}. Mappa e orari personalizzati.`
    },
    lineOrder: cityCfg.lineOrder || c.lineOrder,
    favoriteStops: { ...c.favoriteStops, ...(cityCfg.favoriteStops || {}) },
    activeFocusCity: focusCity,
    activeCityConfig: cityCfg
  };
}

// ─── Public getters ─────────────────────────────────────────────────────────

export function getState(): AppState {
  return state;
}

export function getLineData(): LineDataMap {
  return LINE_DATA;
}

export function setLineData(data: LineDataMap): void {
  LINE_DATA = data;
}

export function mergeLineData(data: Record<string, any>): void {
  LINE_DATA = { ...LINE_DATA, ...data } as LineDataMap;
}

export function getLineConfig(): any {
  return LINE_CONFIG;
}

export function getCFG(): any {
  return CFG;
}
